import { useState } from "react";

const floors = [
  {
    id: "rdc",
    label: "Rez-de-chaussée",
    rooms: [
      { name: "Hall d'entrée", size: "18 m²", icon: "ri-door-open-line", x: "10%", y: "70%", color: "bg-amber-100 text-amber-700 border-amber-200" },
      { name: "Salon / Séjour", size: "75 m²", icon: "ri-sofa-line", x: "30%", y: "40%", color: "bg-sky-100 text-sky-700 border-sky-200" },
      { name: "Cuisine ouverte", size: "42 m²", icon: "ri-restaurant-line", x: "62%", y: "30%", color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
      { name: "Terrasse couverte", size: "60 m²", icon: "ri-sun-line", x: "70%", y: "68%", color: "bg-orange-100 text-orange-700 border-orange-200" },
      { name: "Suite invité", size: "28 m²", icon: "ri-hotel-bed-line", x: "12%", y: "28%", color: "bg-rose-100 text-rose-700 border-rose-200" },
      { name: "Piscine + Spa", size: "80 m²", icon: "ri-drop-line", x: "45%", y: "78%", color: "bg-teal-100 text-teal-700 border-teal-200" },
    ],
    totalSurface: "303 m²",
  },
  {
    id: "r1",
    label: "1er étage",
    rooms: [
      { name: "Suite parentale", size: "55 m²", icon: "ri-hotel-bed-line", x: "28%", y: "25%", color: "bg-violet-100 text-violet-700 border-violet-200" },
      { name: "Dressing", size: "20 m²", icon: "ri-shirt-line", x: "62%", y: "20%", color: "bg-pink-100 text-pink-700 border-pink-200" },
      { name: "Salle de bain spa", size: "22 m²", icon: "ri-shower-line", x: "65%", y: "50%", color: "bg-sky-100 text-sky-700 border-sky-200" },
      { name: "Chambre 2", size: "24 m²", icon: "ri-hotel-bed-line", x: "15%", y: "60%", color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
      { name: "Chambre 3", size: "22 m²", icon: "ri-hotel-bed-line", x: "40%", y: "70%", color: "bg-amber-100 text-amber-700 border-amber-200" },
      { name: "Bureau / Bibliothèque", size: "30 m²", icon: "ri-computer-line", x: "68%", y: "74%", color: "bg-slate-100 text-slate-700 border-slate-200" },
    ],
    totalSurface: "173 m²",
  },
  {
    id: "sous-sol",
    label: "Sous-sol",
    rooms: [
      { name: "Garage double", size: "48 m²", icon: "ri-car-line", x: "18%", y: "40%", color: "bg-slate-100 text-slate-700 border-slate-200" },
      { name: "Cave à vins", size: "18 m²", icon: "ri-goblet-line", x: "52%", y: "28%", color: "bg-rose-100 text-rose-700 border-rose-200" },
      { name: "Salle de sport", size: "35 m²", icon: "ri-run-line", x: "72%", y: "55%", color: "bg-orange-100 text-orange-700 border-orange-200" },
      { name: "Buanderie", size: "12 m²", icon: "ri-loader-line", x: "35%", y: "70%", color: "bg-teal-100 text-teal-700 border-teal-200" },
    ],
    totalSurface: "113 m²",
  },
];

export default function PropertyFloorPlan() {
  const [activeFloor, setActiveFloor] = useState("rdc");
  const [hovered, setHovered] = useState<string | null>(null);

  const floor = floors.find((f) => f.id === activeFloor)!;

  return (
    <div>
      {/* Floor selector tabs */}
      <div className="flex items-center gap-2 mb-6">
        {floors.map((f) => (
          <button
            key={f.id}
            onClick={() => setActiveFloor(f.id)}
            className={`px-4 py-2 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-all duration-200 ${
              activeFloor === f.id
                ? "bg-slate-900 text-white"
                : "bg-slate-100 text-slate-500 hover:bg-slate-200"
            }`}
          >
            {f.label}
          </button>
        ))}
        <span className="ml-auto text-slate-400 text-xs font-medium">
          Surface : <span className="text-slate-700 font-bold">{floor.totalSurface}</span>
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Floor plan visual */}
        <div className="lg:col-span-3 relative bg-slate-50 rounded-2xl overflow-hidden border border-slate-100" style={{ height: "380px" }}>
          {/* Blueprint background */}
          <div
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage: "linear-gradient(#94a3b8 1px, transparent 1px), linear-gradient(90deg, #94a3b8 1px, transparent 1px)",
              backgroundSize: "40px 40px",
            }}
          />

          {/* House outline */}
          <div className="absolute inset-6 border-2 border-slate-300 rounded-xl bg-white/40" />

          {/* Room pins */}
          {floor.rooms.map((room) => (
            <button
              key={room.name}
              onMouseEnter={() => setHovered(room.name)}
              onMouseLeave={() => setHovered(null)}
              className={`absolute cursor-pointer transition-all duration-200 ${hovered === room.name ? "z-10 scale-110" : "z-0"}`}
              style={{ left: room.x, top: room.y, transform: "translate(-50%, -50%)" }}
            >
              <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-semibold shadow-sm ${room.color} ${hovered === room.name ? "shadow-md" : ""}`}>
                <i className={`${room.icon} text-sm`}></i>
                <span className="whitespace-nowrap">{room.size}</span>
              </div>
              {hovered === room.name && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 bg-slate-900 text-white text-xs px-2 py-1 rounded-lg whitespace-nowrap shadow-lg">
                  {room.name}
                </div>
              )}
            </button>
          ))}

          {/* Floor label */}
          <div className="absolute bottom-4 right-4 bg-slate-900 text-white text-xs px-3 py-1.5 rounded-full font-semibold">
            {floor.label}
          </div>
        </div>

        {/* Room list */}
        <div className="lg:col-span-2 flex flex-col gap-2 overflow-y-auto" style={{ maxHeight: "380px" }}>
          <p className="text-slate-500 text-xs uppercase tracking-wider font-semibold mb-1">Pièces</p>
          {floor.rooms.map((room) => (
            <div
              key={room.name}
              onMouseEnter={() => setHovered(room.name)}
              onMouseLeave={() => setHovered(null)}
              className={`flex items-center gap-3 p-3 rounded-xl border cursor-default transition-all duration-200 ${
                hovered === room.name ? `${room.color} scale-[1.01]` : "bg-white border-slate-100 hover:border-slate-200"
              }`}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${hovered === room.name ? "bg-white/60" : "bg-slate-50"}`}>
                <i className={`${room.icon} text-sm ${hovered === room.name ? "" : "text-slate-500"}`}></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-slate-800 text-sm font-medium truncate">{room.name}</p>
              </div>
              <span className={`text-xs font-bold flex-shrink-0 ${hovered === room.name ? "" : "text-slate-500"}`}>{room.size}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
